// Lesson curriculum. Each lesson is a series of "blocks" — prose, interactive demos, or quizzes.
// Kept as data so the lesson player UI is generic.

export const LESSONS = [
  {
    id: 'welcome',
    title: 'Welcome — Why Blackjack',
    blurb: 'The only casino game you can actually beat.',
    blocks: [
      { type: 'prose', text:
`Most casino games are mathematically rigged. The wheel doesn't care what's already happened, the dice forget every roll, and the slot machine pays whatever its chip says.

Blackjack is different — and it's the difference that makes a player edge possible.

The deck has memory. When a 5 comes out, there are fewer 5s left. When a king comes out, there are fewer tens left. The game changes shape as cards are dealt, and a player who tracks that shape can bet more when the deck favors them and less when it doesn't.

That's the whole game. Three skills:

1. **Basic strategy** — playing every hand the mathematically optimal way (worth ~0.5% to you)
2. **Card counting** — knowing when the remaining cards favor the player (worth another ~1% on a good shoe)
3. **Bet sizing** — putting more money out when you have the edge

You can learn all three. This trainer will teach you each piece, then let you practice until it's automatic.` },
      { type: 'cta', label: 'Start with basic strategy →', next: 'basic-strategy' },
    ],
  },

  {
    id: 'basic-strategy',
    title: 'Basic Strategy — The 0.5% Game',
    blurb: 'Cuts the house edge from ~2% (untrained) to ~0.5%. Memorize this and you\'re already better than 95% of players.',
    blocks: [
      { type: 'prose', text:
`Basic strategy is the optimal play for every combination of (your hand) × (dealer's upcard) — assuming a fresh shoe with no count info.

It's a chart. There's no intuition, no "I have a feeling about this one." Whoever computed it ran billions of hands through a computer and found the action with the highest expected value for each cell.

A few rules cover most of it:

- **Hard 12-16 vs dealer 2-6**: stand. Dealer is likely to bust on small upcards.
- **Hard 12-16 vs dealer 7-A**: hit. Dealer is likely to make 17+, you need to improve.
- **Hard 17+**: always stand.
- **11**: double if allowed, else hit. Strongest doubling hand.
- **A,7 (soft 18)**: stand vs 2/7/8, double vs 3-6, **hit** vs 9/10/A. Yes — hit soft 18 vs dealer 9. This one trips everyone up.
- **8s and Aces**: always split.
- **10s**: never split. 20 is too good to break up.
- **5s**: never split. Treat as a 10.

Every other cell is in the chart. Once you've drilled it, you'll never have to think about it.` },
      { type: 'demo', kind: 'strategy-card-flip' },
      { type: 'cta', label: 'On to counting →', next: 'hi-lo' },
    ],
  },

  {
    id: 'hi-lo',
    title: 'Hi-Lo Card Counting',
    blurb: 'The most popular system. Easy to learn, accurate enough to beat the house.',
    blocks: [
      { type: 'prose', text:
`Hi-Lo assigns each card a value:

- **2, 3, 4, 5, 6** → **+1** (low cards — bad for player when they come out)
- **7, 8, 9** → **0** (neutral)
- **10, J, Q, K, A** → **−1** (high cards — good for player when they come out)

You add as cards are dealt. The total is the **running count**.

Why does this matter? When the running count is high (lots of low cards have been removed), the remaining shoe is rich in tens and aces. That's good for you because:

- You make blackjack (3:2 payout) more often
- The dealer busts more often when forced to hit
- Your doubles connect more often

When the running count is low, it's the opposite — bet less or get up.` },
      { type: 'demo', kind: 'count-flash' },
      { type: 'prose', text:
`The system is "balanced" — a complete deck (or shoe) ends at exactly **0**. That's how you check yourself when practicing.

Try the **Count Drill** mode in the nav above. Start with a half deck, get to perfect, then ramp up the speed and deck size.` },
      { type: 'cta', label: 'But wait — true count →', next: 'true-count' },
    ],
  },

  {
    id: 'true-count',
    title: 'True Count — Adjusting for Deck Depth',
    blurb: 'A +6 with two decks left isn\'t the same as +6 with six decks left.',
    blocks: [
      { type: 'prose', text:
`The running count tells you how many "good" cards have been removed, but not how concentrated they are in what's left.

Imagine running count is +6.

- If 6 decks remain: the +6 is spread across ~312 cards. Edge: tiny.
- If 1 deck remains: the +6 is squeezed into ~52 cards. Edge: large.

The fix is the **true count**:

\`true count = running count ÷ decks remaining\`

You estimate decks remaining by glancing at the discard tray.

Most decisions in blackjack key off the *true* count, not running. Bet sizing and deviation plays both use it.` },
      { type: 'demo', kind: 'true-count-demo' },
      { type: 'cta', label: 'Turning count into money →', next: 'bet-ramp' },
    ],
  },

  {
    id: 'bet-ramp',
    title: 'Bet Ramps & Kelly',
    blurb: 'When you have the edge, bet bigger. The math says how much.',
    blocks: [
      { type: 'prose', text:
`Counting is useless if you bet the same amount the whole time. The edge comes from putting more money on the table when the count is high.

A simple **bet ramp** tied to true count:

| True count | Units to bet |
|---|---|
| ≤ +1 | 1 |
| +2 | 2 |
| +3 | 4 |
| +4 | 8 |
| +5 or higher | 12 |

A "unit" is a fraction of your bankroll — typically 0.5% to 2% depending on risk tolerance.

The mathematically optimal sizing is the **Kelly criterion**: bet a fraction equal to your edge divided by the variance of the bet. In practice, players use **half-Kelly** — same growth rate target, but half the volatility. Lower risk of ruin.

Spreads also have to be camouflaged. A 1-to-12 spread is profitable but very obvious to a pit boss. Real-world spreads run 1-to-6 or 1-to-8 to stay under the radar.` },
      { type: 'cta', label: 'When to break basic strategy →', next: 'deviations' },
    ],
  },

  {
    id: 'deviations',
    title: 'Deviations — When Count Overrides Strategy',
    blurb: 'About 80% of card counting\'s edge comes from betting. The remaining 20% comes from deviating.',
    blocks: [
      { type: 'prose', text:
`Basic strategy is built assuming a fresh shoe. When the count is far from neutral, the optimal play for some hands changes.

The most famous: **insurance at TC ≥ +3**. Without a count, insurance is a sucker bet. With true count ≥ +3, the remaining shoe is rich in tens, the dealer is more likely to have a 10 in the hole, and insurance becomes +EV.

Other big ones from the **Illustrious 18**:

- **16 vs 10**: stand at TC ≥ 0 (instead of hitting/surrendering)
- **15 vs 10**: stand at TC ≥ +4
- **12 vs 3**: stand at TC ≥ +2
- **10 vs A**: double at TC ≥ +4
- **11 vs A**: double at TC ≥ +1
- **9 vs 2**: double at TC ≥ +1
- **10 vs 10**: double at TC ≥ +4
- **9 vs 7**: double at TC ≥ +3

You don't need all 18 to capture most of the value. The first 5–6 give you ~95% of the gain.` },
      { type: 'cta', label: 'Bankroll & risk →', next: 'risk' },
    ],
  },

  {
    id: 'risk',
    title: 'Bankroll, Risk of Ruin & Reality',
    blurb: 'You can be a +EV player and still go broke. Math doesn\'t care about your feelings.',
    blocks: [
      { type: 'prose', text:
`Card counting gets you a 0.5–1.5% edge over the house. That's a small edge. Variance is huge.

Two numbers matter:

- **N₀** — the number of hands you need to play before your win is "twice" the standard deviation. Typically ~10,000 hands. Below that, you can be down 100+ units even playing perfectly.
- **Risk of ruin** — probability your bankroll hits zero before reaching your goal.

Rough rules:

- **Bankroll ≥ 200 max bets** for ~5% RoR
- **Bankroll ≥ 400 max bets** for negligible RoR
- **Cap session loss** at ~20% of bankroll. Walk away.

Counters who go broke usually break two rules: they bet too much for their bankroll, and they don't quit when they're stuck. The math will get you there if you let it — but only if you stay solvent long enough for the math to play out.

You're now equipped with the full toolkit. Use the **Table** mode to practice live play with the coach on, **Strategy Drill** to drill the chart, and **Count Drill** to build counting speed. Welcome aboard.` },
      { type: 'cta', label: 'Back to the table', next: null },
    ],
  },
];

export function getLesson(id) {
  return LESSONS.find((l) => l.id === id);
}

export function nextLessonId(currentId) {
  const i = LESSONS.findIndex((l) => l.id === currentId);
  if (i < 0 || i >= LESSONS.length - 1) return null;
  return LESSONS[i + 1].id;
}
